import React, { useContext, useRef, useState } from 'react'
import Agenda from './Agenda';
import Remote from './Remote';
import Program from './Program';
import { AgendaContext } from '../context/AgendaContext';
import { generateAgenda } from '../services/api';
import { MediumButton } from '../styles/Buttons';
import styled from 'styled-components';
import ScrollButton from './ScrollButton';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: minmax(16px, 1fr) [inner-start] minmax(0, 60rem) [inner-end] minmax(16px, 1fr);
    padding: 1rem 0 1rem 0; 

    & > div {
        grid-column: inner; 
    }
`;

const GenerateAgenda = () => {
    const {
        iecInfoDocs, iecActionDocs, participants,
        nextMeeting, selectedStartTime, selectedEndTime, committeeName, lastMeeting, meetingLink, 
        meetingId, meetingPassCode, selectedMeetingType, roles, meetingNumber, previousProtocol, iecClcWgReports,
        clcActionDocs, clcInfoDocs, correspondenceDocs, isAgendaContentVisible, isAgendaContentLoading, plenaryScheduleImage, proposalForSweTitles,
        upComingIntlMeetings, upComingSweMeetings, toggleToc, recentlyDeterminedPubl, isLanguageEn, editableSections
    } = useContext(AgendaContext); 

    const { t } = useTranslation(); 
    const bottomRef = useRef(null);

    const selectedMeetingLocation = () => {
        switch (selectedMeetingType) {
            case 'online': 
                return t('meetingDetails.location.radioButtonLabels.online'); 
            case 'inPerson': 
                return t('meetingDetails.location.radioButtonLabels.inPerson'); 
            case 'hybrid': 
                return t('meetingDetails.location.radioButtonLabels.hybrid'); 
            default: 
                return ''; 
        }; 
    }; 

    const formatEditableSectionItems = items => 
        items.map(item => ({
            title: item.title || '',
            text: item.description || ''
        })); 

    const handleGenerateAgenda = async () => {
        try {
            const committeeInformation = {
                meetings: {
                    last_meeting: lastMeeting || '',
                    next_meeting: nextMeeting[0] || '',
                    meeting_time: `${selectedStartTime} - ${selectedEndTime}` || '',
                },
                members: participants.map(item => {
                    return {
                        absent: item.absent || '',
                        name: item.name || '',
                        note: item.note || '',
                        organization: item.organization || null
                    };
                }),
                roles: roles,
                name: committeeName.value || '',
                title: committeeName.title || ''
            };

            const iecDocs = {
                action_docs: iecActionDocs.map(item => ({
                    action:
                        item.custom_action
                            ? item.custom_action
                            : item.action
                                ? item.action
                                : '',
                    deadline: item.deadline || '',
                    description: item.description || '',
                    document: item.document || '',
                    document_links: item.document_links.map(link => ({
                        fileName: link.fileName || '',
                        url: link.url || ''
                    })),
                })),
                information_docs: iecInfoDocs.map(item => ({
                    document: item.document !== null ? item.document : '',
                    document_links: item.document_links.map(link => ({
                        fileName: link.fileName || '',
                        url: link.url || ''
                    })),
                    published_date: item.published_date || '',
                    title: item.title || ''
                })),
                correspondence_docs: correspondenceDocs.map(item => ({
                    deadline: item.deadline || '',
                    description: item.description || '',
                    document: item.document || '',
                    document_links: item.document_links.map(link => ({
                        fileName: link.fileName || '',
                        url: link.url || ''
                    })),
                    vote: item.vote 
                        ? {
                            ...item.vote, 
                            answer: item.vote.custom_answer || item.vote.answer
                        } 
                        : null,
                })),
                upcoming_intl_meetings: upComingIntlMeetings.map(item => ({
                    end_date: item.end_date || '',
                    name: item.name || '',
                    start_date: item.start_date || '',
                    presence_type: item.presence_type === 'FACE_TO_FACE' ? 'F2F' : item.presence_type || '',
                    registration_deadline: item.registration_deadline || '',
                    registration_url: item.registration_url || '',
                    location: item.location || '',
                    registration_open: item.registration_open || ''
                })),
                upcoming_swe_meetings: upComingSweMeetings.map(item => ({
                    end_date: item.end_date || '',
                    name: item.name || '',
                    start_date: item.start_date || '',
                    presence_type: item.presence_type === 'FACE_TO_FACE' ? 'F2F' : item.presence_type || '',
                    location: item.location || '',
                }))
            };

            const onlineMeeting = {
                url: meetingLink || '',
                id: meetingId || '',
                passphrase: meetingPassCode || ''
            }

            const otherInfoAndActivities = formatEditableSectionItems(editableSections.otherInfoAndActivities)
            const anyOtherBusiness = formatEditableSectionItems(editableSections.anyOtherBusiness)
            const recurringTopics = formatEditableSectionItems(editableSections.recurringTopics)

            const generalAgendaData = {
                meeting_number: meetingNumber || '',
                location: selectedMeetingLocation() || '',
                previous_protocol: previousProtocol || '',
                schedule_image: plenaryScheduleImage || '',
                table_of_content: {
                    show: toggleToc 
                },
                language: { 
                    en: isLanguageEn
                },
            };

            const reportsIecClc = iecClcWgReports.map(item => ({
                group: item.group || '',
                agent: item.agent || '',
                activity: {
                    text: item.activity.link.text || '',
                    url: item.activity.link.url || '',
                },
                notes: item.activity.notes || '',
            }))

            const clcDocs = {
                action_docs: clcActionDocs.map(item => ({
                    reference: item.reference || '',
                    title: item.title || '',
                    deadline: item.deadline || '',
                    document_links: item.document_links.map(link => ({
                        fileName: link.fileName || '',
                        url: link.url || ''
                    })),
                    action:
                        item.custom_action
                            ? item.custom_action
                            : item.action
                                ? item.action
                                : ''

                })),
                information_docs: clcInfoDocs.map(item => ({
                    reference: item.reference || '',
                    title: item.title || '',
                    published: item.published_date || '',
                    document_links: item.document_links.map(link => ({
                        fileName: link.fileName || '',
                        url: link.url || ''
                    })),
                }))
            };

            await generateAgenda(
                committeeInformation, 
                iecDocs, 
                onlineMeeting, 
                otherInfoAndActivities, 
                anyOtherBusiness, 
                recurringTopics, 
                generalAgendaData, 
                reportsIecClc, 
                clcDocs, 
                proposalForSweTitles, 
                recentlyDeterminedPubl
            );

        } catch (error) {
            console.error("Error generating agenda:", error)
        }
    };

    return (
        <Wrapper>
            <div>
                <Agenda />
                {isAgendaContentVisible && !isAgendaContentLoading && (
                    <>
                        <Remote />
                        <Program />
                        <MediumButton onClick={handleGenerateAgenda}>{t('generateMicrosoftDocument')}</MediumButton>    
                        <ScrollButton bottomRef={bottomRef} />
                        <div ref={bottomRef}></div>
                    </>
                )}
            </div>
        </Wrapper>
    )
}

export default GenerateAgenda